import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { TooltipContent } from '@design-system';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import type { StrategyPresentation } from './types';

// NYL Asset Flex presentation: standardizes on the shared
// BalanceBreakdownTooltip for pool / death benefit cells; adds a
// reimbursement-rule explanation on the payout cell.

// Renders a static "$Y/mo × Z mo = $X" note for a duration-derived pool.
// Returns undefined for legacy policies where duration is not known.
function poolAtIssueNote(
  baseMonthly: number,
  durationMonths: number | undefined,
  atIssue: number | undefined,
): string | undefined {
  if (durationMonths === undefined || atIssue === undefined) return undefined;
  return `At issue: ${currency(baseMonthly)}/mo × ${durationMonths} mo = ${currency(atIssue)}`;
}

export const nylAssetFlexPresentation: StrategyPresentation = {
  poolCellTooltip: ({ month, openState, terms }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    const eobUntouched = openEob > 0 && closeEob === openEob && eobAccrual === 0;

    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    const policyYear = month.context.policyYear;
    const baseAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.baseBenefitPoolDurationMonths,
      terms.baseBenefitPoolAtIssue,
    );
    const eobAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.extensionOfBenefitsPoolDurationMonths,
      terms.extensionOfBenefitsPoolAtIssue,
    );
    const eobNoteParts = [
      eobAtIssueNote,
      eobUntouched ? 'Not yet engaged' : undefined,
    ].filter(Boolean);

    return (
      <BalanceBreakdownTooltip
        title="LTC pool breakdown"
        description="Asset Flex pays from the Acceleration Benefit first; the Extension of Benefits pool takes over once Acceleration is exhausted. ACIO grows both pools at the policy anniversary."
        sections={[
          {
            heading: `Acceleration Benefit · Policy year ${policyYear}`,
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'ACIO accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
            note: baseAtIssueNote,
          },
          {
            heading: `Extension of Benefits · Policy year ${policyYear}`,
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'ACIO accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
            note: eobNoteParts.length > 0 ? eobNoteParts.join(' · ') : undefined,
          },
        ]}
        footer="Pool dollars are tracked through reimbursement and inflated by ACIO each anniversary. May diverge slightly from the carrier illustration's printed forward-inflated pool totals."
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const accelDrawdown = month.deltas.base.depletion;
    if (open === 0 && close === 0 && reduction === 0) return null;

    // When the Residual floor capped the reduction, the raw Acceleration
    // drawdown is bigger than the actual DB depletion. Surface that gap so
    // the advisor sees why Opening − Drawn ≠ Closing in this row.
    const floorNote = flooredAtMinimum
      ? `Acceleration drew ${currency(accelDrawdown)}; depletion capped at the Residual Death Benefit (${currency(close)}).`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="Flat Face Amount, depleted dollar-for-dollar by Acceleration Benefit drawdown. EOB drawdown does NOT deplete the death benefit. Floors at the Residual Death Benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              {
                label: 'Reduced this month',
                value: reduction,
                kind: 'subtract',
              },
            ],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const cost = month.facts.effectiveCareCost;
    const eligibleDays = month.facts.coverageEligibleDays;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    if (eligibleDays === 0) return null;

    // Reimbursement is computed against pro-rated cost (eligibleDays ×
    // dailyCareCost), not the full-month cost. Surface that distinction so
    // partial-eligibility months don't mislabel the cost basis or
    // overstate OOP attribution to "policy didn't cover".
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    const ineligibleDayCost = Math.max(cost - eligibleCost, 0);
    const eligibleOop = Math.max(eligibleCost - paid, 0);

    let rule: string;
    if (paid === 0 && closeBase === 0 && closeEob === 0) {
      rule = 'Both LTC pools are exhausted — the policy reimburses nothing; the full care cost is out-of-pocket.';
    } else if (paid >= eligibleCost - 0.01) {
      rule = 'Care cost is at or below the monthly benefit cap. Since Asset Flex is a reimbursement product, the policy pays the actual eligible cost.';
    } else if (paid > 0) {
      rule = 'Care cost exceeds the monthly benefit cap. Asset Flex reimburses up to the cap; the excess is out-of-pocket.';
    } else {
      rule = 'No reimbursement this month.';
    }

    const drawnFrom: string[] = [];
    if (drawnBase > 0) drawnFrom.push(`Acceleration ${currency(drawnBase)}`);
    if (drawnEob > 0) drawnFrom.push(`Extension of Benefits ${currency(drawnEob)}`);

    const proratedNote = isPartial && paid > 0
      ? `Pro-rated for ${eligibleDays} eligible day${eligibleDays === 1 ? '' : 's'} (waiting period or <2 ADL consumed the rest of the month).`
      : null;

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — reimbursement</div>
        <div className="mb-1 text-[10px] text-gray-300">{rule}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Care cost this month</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartial && (
            <>
              <span className="text-gray-300">Eligible portion</span>
              <span className="font-semibold tabular-nums">
                {currency(eligibleCost)}
                <span className="ml-1 font-normal text-gray-400">
                  ({eligibleDays}d × {currency(dailyCost)})
                </span>
              </span>
            </>
          )}
          <span className="text-gray-300">Reimbursed</span>
          <span className="font-semibold tabular-nums">{currency(paid)}</span>
          {eligibleOop > 0 && (
            <>
              <span className="text-gray-300">
                {isPartial ? 'OOP (over cap)' : 'Out-of-pocket'}
              </span>
              <span className="font-semibold tabular-nums text-rose-300">
                {currency(eligibleOop)}
              </span>
            </>
          )}
          {ineligibleDayCost > 0 && (
            <>
              <span className="text-gray-300">OOP (ineligible days)</span>
              <span className="font-semibold tabular-nums text-rose-300">
                {currency(ineligibleDayCost)}
              </span>
            </>
          )}
        </div>
        {drawnFrom.length > 0 && (
          <div className="mt-1 text-[10px] text-gray-400">
            Drawn from: {drawnFrom.join(' + ')}
          </div>
        )}
        {proratedNote && (
          <div className="mt-1 text-[10px] text-gray-400">{proratedNote}</div>
        )}
      </TooltipContent>
    );
  },

  // Asset Flex's monthly cap inflates by ACIO on the On-Face amount
  // (`baseBenefitPoolInflationRate`), which can differ from the policy-wide
  // ACIO when an On-Face override is set. Use that rate in the tooltip so
  // the `base × (1+r)^n` math reconciles with the displayed cap.
  benefitInflationRate: terms =>
    terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  benefitAmountCellTooltipFooter: ({ snapshot, terms }) => {
    // Surface the link from this year's monthly cap to the inflated pool
    // ceilings so advisors can see where the Acceleration / EOB headline
    // dollars come from. Only show the "× N mo" derivation when the
    // duration-derived model is active (legacy policies have no months to
    // multiply by).
    const accelMonths = terms.baseBenefitPoolDurationMonths;
    const eobMonths = terms.extensionOfBenefitsPoolDurationMonths;
    if (accelMonths === undefined && eobMonths === undefined) return null;

    const monthlyCap = snapshot.monthlyBenefitCap;
    return (
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-[10px]">
        <span className="col-span-2 text-gray-300">
          Pool ceiling at this monthly cap
        </span>
        {accelMonths !== undefined && (
          <>
            <span className="text-gray-300">
              Acceleration × {accelMonths} mo
            </span>
            <span className="font-semibold tabular-nums">
              {currency(monthlyCap * accelMonths)}
            </span>
          </>
        )}
        {eobMonths !== undefined && eobMonths > 0 && (
          <>
            <span className="text-gray-300">EOB × {eobMonths} mo</span>
            <span className="font-semibold tabular-nums">
              {currency(monthlyCap * eobMonths)}
            </span>
          </>
        )}
      </div>
    );
  },

  copy: {
    poolTooltip:
      'End-of-month LTC pool balance. Asset Flex pays from the Acceleration Benefit first, then the Extension of Benefits pool. ACIO grows both pools at the policy anniversary.',
    payoutColumnTooltip:
      'Reimbursement payout this month (capped at the monthly benefit; pro-rated for partial-eligibility months), with cumulative dollars paid by the policy through this month in parentheses.',
    benefitInflationCompoundLabel: 'Compound ACIO growth',
    benefitInflationCapNote: capYear => `ACIO capped at year ${capYear}`,
  },
};
